import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, KeyRound, PlugZap } from "lucide-react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import SuperHeader, { apiErr, fmtDate } from "@/components/superadmin/SuperHeader";
import { useAdminFetch, TabState, Stat } from "@/components/superadmin/useAdminFetch";
import ConfirmDialog from "@/components/superadmin/ConfirmDialog";
import GoogleAdsCard from "@/components/superadmin/GoogleAdsCard";

function KeyCard({ i, reload }) {
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const call = async (name, fn, ok) => {
    if (busy) return;
    setBusy(name);
    try { const r = await fn(); toast[r?.data?.ok === false ? "error" : "success"](r?.data?.message || ok); setKey(""); reload(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(null); }
  };
  const put = (body, ok, name = "save") => call(name, () => api.put("/admin/integrations/google", body), ok);
  return (
    <Card className="p-5 border-slate-200 space-y-4" data-testid="google-integration-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2"><PlugZap className="w-5 h-5 text-indigo-600" /><h2 className="font-semibold">Google Places + YouTube (platform key)</h2></div>
        <div className="flex gap-2">
          <Badge className={i.configured ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"} data-testid="google-key-state">{i.configured ? `Key saved · ${i.key_masked}` : "No key"}</Badge>
          <Badge className={i.enabled ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"} data-testid="google-enabled-state">{i.enabled ? "Enabled" : "Disabled"}</Badge>
        </div>
      </div>
      <p className="text-xs text-slate-500">One key is used for all companies. It is stored encrypted on the server and never sent to any browser. Enable "Places API (New)" and "YouTube Data API v3" on it in Google Cloud.</p>
      <div className="flex flex-wrap gap-2">
        <Input type="password" value={key} onChange={(e) => setKey(e.target.value)} placeholder={i.configured ? "Paste a new key to replace" : "Paste Google API key"} className="max-w-md" data-testid="google-key-input" />
        <Button onClick={() => key.trim() ? put({ api_key: key.trim() }, "Key saved") : toast.error("Paste a key first")} disabled={!!busy} className="bg-indigo-600 hover:bg-indigo-700" data-testid="google-key-save">
          {busy === "save" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <KeyRound className="w-4 h-4 mr-1" />}Save key
        </Button>
        <Button variant="outline" disabled={!i.configured || !!busy} onClick={() => call("test", () => api.post("/admin/integrations/google/test"), "Connection OK")} data-testid="google-test">
          {busy === "test" && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Test connection
        </Button>
        {i.configured && <Button variant="ghost" className="text-rose-600" disabled={!!busy} data-testid="google-key-remove"
          onClick={() => setConfirm({ title: "Remove the platform Google key?", body: "Google and YouTube search will stop for every company until a new key is saved.", danger: true, label: "Remove key", run: () => put({ api_key: "" }, "Key removed", "remove") })}>Remove key</Button>}
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="flex items-center justify-between p-3 rounded-lg border border-slate-200 text-sm">
          <span><b>Business Finder (Google)</b><span className="block text-xs text-slate-500">Turn off to stop all companies from using Google search</span></span>
          <Switch checked={!!i.enabled} disabled={!!busy} onCheckedChange={(v) => put({ enabled: v }, v ? "Integration enabled" : "Integration disabled")} data-testid="google-enabled-switch" />
        </label>
        <label className="flex items-center justify-between p-3 rounded-lg border border-slate-200 text-sm">
          <span><b>YouTube channel search</b><span className="block text-xs text-slate-500">Uses the same key</span></span>
          <Switch checked={!!i.youtube_enabled} disabled={!!busy} onCheckedChange={(v) => put({ youtube_enabled: v }, v ? "YouTube enabled" : "YouTube disabled")} data-testid="youtube-enabled-switch" />
        </label>
      </div>
      <div className="text-xs text-slate-600 space-y-1" data-testid="google-health">
        <div>Last test: {i.last_test_at ? `${fmtDate(i.last_test_at)} · ${i.last_test_ok ? "OK" : "Failed"}` : "never"}{i.last_test_message ? ` — ${i.last_test_message}` : ""}</div>
        {i.last_error && <div className="text-rose-700">Last search error ({fmtDate(i.last_error_at)}): {i.last_error}</div>}
      </div>
      <ConfirmDialog confirm={confirm} onClose={() => setConfirm(null)} />
    </Card>
  );
}

export default function SuperIntegrations() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const { data, error, loading, reload } = useAdminFetch("/admin/integrations/google", { month });
  return (
    <div className="min-h-screen bg-slate-50" data-testid="super-integrations-page">
      <SuperHeader />
      <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-5">
        <h1 className="text-2xl font-bold text-slate-900">Integrations</h1>
        <TabState loading={loading && !data} error={error} testid="integrations">
          {data && <>
            <KeyCard i={data.integration} reload={reload} />
            <GoogleAdsCard />
            <Card className="p-5 border-slate-200 space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="font-semibold">Usage by company</h2>
                <Input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} className="w-44" data-testid="usage-month" />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-6 gap-2">
                <Stat label="Searches" value={data.totals.searches} testid="usage-total-searches" />
                <Stat label="Google" value={data.totals.google} /><Stat label="YouTube" value={data.totals.youtube} />
                <Stat label="Businesses returned" value={data.totals.results} /><Stat label="Failed calls" value={data.totals.errors} />
                <Stat label="Companies using it" value={data.totals.companies} />
              </div>
              <p className="text-xs text-slate-500">{data.disabled_companies} companies disabled · {data.limited_companies} with a monthly limit (others unlimited). Set these in a company's Settings tab. Demo searches are not counted.</p>
              {data.usage.length === 0 ? <div className="p-6 text-center text-sm text-slate-500" data-testid="usage-empty">No searches in {data.month}.</div> : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[700px]">
                    <thead className="bg-slate-50 text-xs uppercase text-slate-600"><tr><th className="p-2 text-left">Company</th><th className="p-2 text-center">Searches</th><th className="p-2 text-center">Google</th><th className="p-2 text-center">YouTube</th><th className="p-2 text-center">Results</th><th className="p-2 text-center">Errors</th><th className="p-2 text-left">Limit</th></tr></thead>
                    <tbody>{data.usage.map((u) => (
                      <tr key={u.company_id} className="border-t border-slate-100" data-testid={`usage-row-${u.company_id}`}>
                        <td className="p-2"><Link to={`/super-admin/companies/${u.company_id}?tab=settings`} className="text-indigo-700 hover:underline">{u.company_name}</Link>{!u.enabled && <Badge className="ml-2 bg-rose-100 text-rose-700 text-[10px]">disabled</Badge>}</td>
                        <td className="p-2 text-center font-medium">{u.searches}</td><td className="p-2 text-center">{u.google}</td><td className="p-2 text-center">{u.youtube}</td>
                        <td className="p-2 text-center">{u.results}</td><td className="p-2 text-center">{u.errors}</td>
                        <td className="p-2 text-xs">{u.monthly_limit ?? "Unlimited"}</td>
                      </tr>))}</tbody>
                  </table>
                </div>
              )}
            </Card>
          </>}
        </TabState>
      </div>
    </div>
  );
}
